import React, { useEffect, useState } from "react";
import { normalizeSearchText } from "~/utility/volby2026";

type Municipality = { code: string; label: string };

type Props = {
  municipalities: Municipality[];
  selectedLabel: string | null;
  onSelect: (code: string, label: string) => void;
  onClear: () => void;
};

const MunicipalitySearch = ({ municipalities, selectedLabel, onSelect, onClear }: Props) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);

  // When a municipality gets selected (e.g. picked from the dropdown), show
  // its label in the (still editable) input.
  useEffect(() => {
    if (selectedLabel) {
      setQuery(selectedLabel);
      setOpen(false);
    }
  }, [selectedLabel]);

  const normalizedQuery = normalizeSearchText(query);

  // Match the whole label by prefix (same as volby.statistics.sk), but also
  // at any word boundary inside it — so "rusovce" finds
  // "Bratislava - Rusovce (okres Bratislava V)" and "stare mesto" finds
  // "Bratislava - Staré Mesto (okres Bratislava I)", not just names whose
  // full label starts with the query.
  const matches =
    normalizedQuery.length >= 3
      ? (() => {
          const starting: Municipality[] = [];
          const containing: Municipality[] = [];
          municipalities.forEach((m) => {
            const normLabel = normalizeSearchText(m.label);
            if (normLabel.startsWith(normalizedQuery)) {
              starting.push(m);
              return;
            }
            const wordBoundaries = /[a-z0-9]+/g;
            let word: RegExpExecArray | null;
            while ((word = wordBoundaries.exec(normLabel))) {
              if (word.index > 0 && normLabel.slice(word.index).startsWith(normalizedQuery)) {
                containing.push(m);
                return;
              }
            }
          });
          return [...starting, ...containing].slice(0, 20);
        })()
      : [];

  const handleSelect = (m: Municipality) => {
    onSelect(m.code, m.label);
    setOpen(false);
    setHighlighted(0);
  };

  const handleChange = (value: string) => {
    setQuery(value);
    setOpen(true);
    setHighlighted(0);
    // The user is overwriting/erasing an already-selected obec by typing —
    // treat it as cleared so stale results don't linger under a new query.
    if (selectedLabel && value !== selectedLabel) {
      onClear();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!matches.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHighlighted((i) => (i + 1) % matches.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      setHighlighted((i) => (i - 1 + matches.length) % matches.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const exact = matches.find((m) => normalizeSearchText(m.label) === normalizedQuery);
      handleSelect(exact || matches[highlighted] || matches[0]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const handleClearClick = () => {
    onClear();
    setQuery("");
  };

  return (
    <div className="v26-search-wrapper">
      <div className="v26-search-title">Vyhľadať obec</div>
      <div className="v26-search-row">
        <input
          type="text"
          value={query}
          placeholder="Zadajte názov obce"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
        {selectedLabel && (
          <button type="button" onClick={handleClearClick}>
            Vyčistiť
          </button>
        )}
      </div>
      {!selectedLabel && <div className="v26-search-hint">Zadajte aspoň 3 znaky</div>}
      {open && matches.length > 0 && (
        <div className="v26-search-dropdown">
          {matches.map((m, i) => (
            <div
              key={m.code}
              className={"v26-search-option" + (i === highlighted ? " highlighted" : "")}
              onMouseEnter={() => setHighlighted(i)}
              onMouseDown={() => handleSelect(m)}
            >
              {m.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MunicipalitySearch;
